// =============================================================
// 檔案名稱：regionUtils.js
// 版本：V1.01
// 日期：2025/07/19
// 主要功能與職責：
//   區域權限系統的工具函數，提供縣市轉換、權限檢查等實用功能。
// 這個檔案是做什麼的？
//   提供區域相關的工具函數，包含縣市到區域的轉換、權限驗證、
//   區域篩選等功能，讓其他模組能方便地使用區域功能。
// 解決了什麼問題？
//   統一提供區域相關的工具函數，避免重複程式碼，提升開發效率。
// 更新歷史：
// V1.01 - 初版建立
// 注意事項：
//   此檔案依賴 regions.js 常數，修改時需確保相容性。
// 關連檔案：regions.js, AdminPanel.js, BookingModal.js
// =============================================================

import { 
    CITY_TO_REGION, 
    REGION_TO_CITIES, 
    REGION_NAMES, 
    REGION_ICONS, 
    REGION_COLORS,
    ALL_REGIONS,
    REGION_CODES
} from '../constants/regions';
import { isAdmin } from './roleUtils';

/**
 * 根據縣市名稱取得對應的區域代碼
 * @param {string} city - 縣市名稱
 * @returns {string|null} 區域代碼，如果找不到則返回 null
 */
export const getRegionByCity = (city) => {
    return CITY_TO_REGION[city] || null;
};

/**
 * 根據區域代碼取得對應的縣市陣列
 * @param {string} regionCode - 區域代碼
 * @returns {string[]} 縣市陣列
 */
export const getCitiesByRegion = (regionCode) => {
    return REGION_TO_CITIES[regionCode] || [];
};

/**
 * 檢查夥伴是否有指定區域的權限
 * @param {string[]} userRegions - 夥伴的區域權限陣列
 * @param {string} targetRegion - 目標區域代碼
 * @returns {boolean} 是否有權限
 */
export const hasRegionPermission = (userRegions, targetRegion) => {
    if (!userRegions || !Array.isArray(userRegions)) return false;
    return userRegions.includes(targetRegion);
};

/**
 * 檢查夥伴是否有指定縣市的權限
 * @param {string[]} userRegions - 夥伴的區域權限陣列
 * @param {string} city - 縣市名稱
 * @returns {boolean} 是否有權限
 */
export const hasCityPermission = (userRegions, city) => {
    const cityRegion = getRegionByCity(city);
    if (!cityRegion) return false;
    return hasRegionPermission(userRegions, cityRegion);
};

/**
 * 根據夥伴權限篩選市場列表
 * @param {Array} markets - 市場列表
 * @param {string[]} userRegions - 夥伴的區域權限陣列
 * @returns {Array} 篩選後的市場列表
 */
export const filterMarketsByPermission = (markets, userRegions, user) => {
    if (isAdmin(user)) return markets;
    if (!userRegions || !Array.isArray(userRegions) || userRegions.length === 0) {
        return [];
    }
    
    return markets.filter(market => {
        // 縣市是區域分類的單一依據；舊資料的 region 可能缺漏或尚未修正。
        if (market.city) {
            return hasCityPermission(userRegions, market.city);
        }

        return market.region ? hasRegionPermission(userRegions, market.region) : false;
    });
};

/**
 * 根據夥伴權限篩選預約列表
 * @param {Array} bookings - 預約列表
 * @param {Array} markets - 市場列表
 * @param {string[]} userRegions - 夥伴的區域權限陣列
 * @returns {Array} 篩選後的預約列表
 */
export const filterBookingsByPermission = (bookings, markets, userRegions, user) => {
    if (isAdmin(user)) return bookings;
    if (!userRegions || !Array.isArray(userRegions) || userRegions.length === 0) {
        return [];
    }
    
    // 建立市場ID到區域的對應表
    const marketRegionMap = {};
    markets.forEach(market => {
        if (market.city) {
            marketRegionMap[market.id] = getRegionByCity(market.city);
        } else if (market.region) {
            marketRegionMap[market.id] = market.region;
        }
    });
    
    return bookings.filter(booking => {
        const marketRegion = marketRegionMap[booking.marketId];
        if (!marketRegion) return false;
        return hasRegionPermission(userRegions, marketRegion);
    });
};

/**
 * 取得區域的顯示名稱
 * @param {string} regionCode - 區域代碼
 * @returns {string} 區域顯示名稱
 */
export const getRegionDisplayName = (regionCode) => {
    return REGION_NAMES[regionCode] || regionCode;
};

/**
 * 取得區域的圖示
 * @param {string} regionCode - 區域代碼
 * @returns {string} 區域圖示
 */
export const getRegionIcon = (regionCode) => {
    return REGION_ICONS[regionCode] || '📍';
};

/**
 * 取得區域的顏色
 * @param {string} regionCode - 區域代碼
 * @returns {string} 區域顏色
 */
export const getRegionColor = (regionCode) => {
    return REGION_COLORS[regionCode] || 'gray';
};

/**
 * 格式化區域權限陣列為顯示文字
 * @param {string[]} regions - 區域權限陣列
 * @returns {string} 格式化的顯示文字
 */
export function formatRegionsForDisplay(regions) {
  if (!regions || regions.length === 0) return '無';
  return regions.map(region => {
    const name = getRegionDisplayName(region);
    const icon = getRegionIcon(region);
    let color = '';
    switch(region) {
      case 'north': color = 'bg-blue-200 text-blue-800'; break;
      case 'central': color = 'bg-yellow-200 text-yellow-800'; break;
      case 'south': color = 'bg-green-200 text-green-800'; break;
      case 'east': color = 'bg-purple-200 text-purple-800'; break;
      default: color = 'bg-gray-200 text-gray-800';
    }
    return `<span class='inline-block px-2 py-0.5 rounded ${color} mr-1 mb-1'>${icon} ${name}</span>`;
  }).join('');
}

/**
 * 檢查是否為全區權限
 * @param {string[]} regions - 區域權限陣列
 * @returns {boolean} 是否為全區權限
 */
export const isAllRegionsPermission = (regions) => {
    if (!regions || !Array.isArray(regions)) return false;
    return regions.length === ALL_REGIONS.length && 
           ALL_REGIONS.every(region => regions.includes(region));
};

/**
 * 取得夥伴可訪問的縣市列表
 * @param {string[]} userRegions - 夥伴的區域權限陣列
 * @returns {string[]} 可訪問的縣市列表
 */
export const getAccessibleCities = (userRegions) => {
    if (!userRegions || !Array.isArray(userRegions)) return [];
    
    const cities = [];
    userRegions.forEach(region => {
        const regionCities = getCitiesByRegion(region);
        cities.push(...regionCities);
    });
    
    return [...new Set(cities)]; // 去重
};

/**
 * 驗證區域代碼是否有效
 * @param {string} regionCode - 區域代碼
 * @returns {boolean} 是否有效
 */
export const isValidRegionCode = (regionCode) => {
    return ALL_REGIONS.includes(regionCode);
};

/**
 * 驗證區域權限陣列是否有效
 * @param {string[]} regions - 區域權限陣列
 * @returns {boolean} 是否有效
 */
export const isValidRegionsArray = (regions) => {
    if (!Array.isArray(regions)) return false;
    return regions.every(region => isValidRegionCode(region));
};
